import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import DashboardLayout from './components/layout/DashboardLayout';
import MetricsViewer from './components/metrics-viewer/page/MetricsViewer';
import LoginPage from './components/pages/LoginPage';
import PrivateRoute from './components/route/PrivateRoute';
import { ConfigUtil } from './config/config-util';
import { AuthProvider } from './providers/AuthProvider';
import { ClientProvider } from './providers/ClientProvider';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#040d69',
    },
    secondary: {
      main: '#2438b3',
    },
    background: {
      default: '#F5F6F8',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
    },
  },
  typography: {
    fontFamily: '"Noto Sans JP", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#ffffff',
          color: '#000000',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: 1.5,
          maxWidth: '300px',
          padding: '12px 16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
        },
        arrow: {
          color: '#ffffff',
          '&::before': {
            border: '1px solid #e0e0e0',
          },
        },
      },
    },
  },
});

const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <GoogleOAuthProvider clientId={ConfigUtil.getGoogleCliendId()}>
          <BrowserRouter>
            <AuthProvider>
              <ClientProvider>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route element={<PrivateRoute />}>
                    <Route
                      path="/dashboard/*"
                      element={
                        <DashboardLayout>
                          <Routes>
                            <Route
                              path="metrics-viewer"
                              element={<MetricsViewer />}
                            />
                            <Route
                              path="*"
                              element={
                                <Navigate
                                  to="/dashboard/metrics-viewer"
                                  replace
                                />
                              }
                            />
                          </Routes>
                        </DashboardLayout>
                      }
                    />
                  </Route>
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </ClientProvider>
            </AuthProvider>
          </BrowserRouter>
        </GoogleOAuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
